import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, TextField } from '@mui/material';
import { useConfig, useNotification } from '@revenuewell/front-end-bundle';
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import DirectCommunicationService from '../../../../../services/practice-portal/direct-communication/direct-communication-service';
import { DirectCommunicationType } from '../../../../../services/practice-portal/direct-communication/types';
import { Card, CardTitle } from '../../../card/card.style';
import { CloseDialogButton } from '../../../dialog/dialog.styles';
import { TemplateEditor, TemplateEditorRefProps } from '../template-editor';
import { QuickEmailDialogSkeleton } from './quick-email-dialog.skeleton';

const TEMPLATE_BODY_SECTION_ID = 100;

export interface QuickEmailDialogProps {
  open: boolean;
  onClose?: () => void;
  onSend?: () => void;
  patientId: number;
  locationId: number;
  title?: string;
  fromName?: string;
  subject?: string;
  body?: string;
}

export const QuickEmailDialog: FC<QuickEmailDialogProps> = (props) => {
  const { open, onClose, onSend, patientId, locationId, title, body, subject, fromName } = props;
  const { config } = useConfig();
  const { showError, showSuccess } = useNotification();
  const [loading, setLoading] = useState<boolean>(false);
  const [templateUrl, setTemplateUrl] = useState<string | null>(null);
  const [templateReady, setTemplateReady] = useState<boolean>(false);
  const [templateSubject, setTemplateSubject] = useState<string>(subject || '');
  const [templateBody, _setTemplateBody] = useState<string>(body || '');
  const templateSubjectRef = useRef(templateSubject);
  const templateEditorRef = useRef<TemplateEditorRefProps>(null);
  const directCommunicationService = useMemo(() => new DirectCommunicationService(config), [config]);

  const templateBodyRef = useRef(templateBody);
  const setTemplateBody = (text: string) => {
    templateBodyRef.current = text;
    _setTemplateBody(text);
  };

  useEffect(() => {
    templateSubjectRef.current = templateSubject;
  }, [templateSubject]);

  useEffect(() => {
    if (open) {
      if (!patientId || !locationId) return;
      loadTemplate(locationId, patientId);
    } else {
      setTemplateUrl(null);
      setTemplateSubject(subject || '');
      setTemplateBody(body || '');
      setTemplateReady(false);
    }
  }, [open, patientId, locationId]);

  const loadTemplate = async (locationId: number, patientId: number) => {
    try {
      const templateId = await directCommunicationService.getDirectCommunicationTemplateId(
        locationId,
        DirectCommunicationType.CustomMessage
      );
      const url = `${config.practice.url}/DirectCommunication/GetTemplateEditor?idTemplate=${templateId}&idRecipient=${patientId}`;
      setTemplateUrl(url);
    } catch {
      showError('Unable to generate template editor url.');
      onClose && onClose();
    }
  };

  const handleEditorReady = () => setTemplateReady(true);

  const handleEditorError = (status: any, message: any) => {
    console.error('Template Editor error: ', message, status);
    showError('Template Editor error has occured.');
  };

  const handleTemplateDataReceived = async (templateData: any) => {
    try {
      const data = injectBodyTextIntoTemplate(templateBodyRef.current, templateData);
      const payload = {
        Subject: templateSubjectRef.current,
        FromName: `From ${fromName}`,
        InternalTemplateData: data
      };
      const result = await directCommunicationService.createDirectCommunication(patientId, payload);
      if (!result.IsSuccess) throw new Error();
      showSuccess('The email was sent.');
      onSend && onSend();
    } catch (error) {
      console.error('Unable to send email: ', error);
      showError('Unable to send email.');
      onClose && onClose();
    } finally {
      setLoading(false);
    }
  };

  const injectBodyTextIntoTemplate = (text: string, tData: any) => {
    const tBodySectionStack = tData.SectionStacks.find((x: any) => x.IdSectionStack == TEMPLATE_BODY_SECTION_ID);
    const tBodySection = tBodySectionStack.Sections[0];
    const tBodySectionValue = tBodySection.SectionValues[0];

    tBodySectionValue.Value = text.replace(/\n/g, '<br>');
    return tData;
  };

  const handleSaveClick = () => {
    setLoading(true);
    if (templateEditorRef.current) templateEditorRef.current.getTemplateData();
  };

  const handleClose = () => onClose && onClose();

  const handleSujectChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTemplateSubject(e.target.value);
  };

  const handleBodyChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTemplateBody(e.target.value);
  };

  return (
    <>
      <Dialog open={open} maxWidth={false}>
        <DialogContent sx={{ width: '600px', backgroundColor: '#FBFBFB' }}>
          <CardTitle sx={{ cursor: 'default', margin: '20px 20px 0 20px', width: 'auto' }}>
            {title ?? 'Send Email'}
            <CloseDialogButton onClick={handleClose} />
          </CardTitle>
          <Card sx={{ margin: '20px 20px 20px 20px', padding: '20px' }}>
            {!templateReady && <QuickEmailDialogSkeleton />}
            {templateReady && (
              <>
                <TextField
                  size="small"
                  value={templateSubject}
                  onChange={handleSujectChange}
                  label="Subject"
                  fullWidth
                  sx={{ marginTop: '10px' }}
                />
                <TextField
                  size="small"
                  multiline
                  fullWidth
                  rows={6}
                  value={templateBody}
                  label="Body"
                  onChange={handleBodyChange}
                  sx={{ marginTop: '20px' }}
                />
                <DialogActions sx={{ padding: 0, marginTop: '35px' }}>
                  <Button
                    onClick={handleClose}
                    color="primary"
                    variant="outlined"
                    disableElevation
                    sx={{ marginRight: '12px' }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    type="submit"
                    loading={loading}
                    disabled={!templateReady || templateSubject.length === 0 || templateBody.length === 0}
                    onClick={handleSaveClick}
                    color="primary"
                    variant="contained"
                    disableElevation
                    sx={{ marginLeft: '0' }}
                  >
                    Send
                  </LoadingButton>
                </DialogActions>
              </>
            )}
          </Card>
        </DialogContent>
      </Dialog>

      <TemplateEditor
        ref={templateEditorRef}
        url={templateUrl}
        onReady={handleEditorReady}
        onError={handleEditorError}
        onTemplateDataReceived={handleTemplateDataReceived}
        sx={{ visibility: 'hidden', height: 0, width: 0, margin: 0 }}
      />
    </>
  );
};
